import formatDate from '@smart-tv/utils/date/format-date';
import { SeekableRange } from '../../types';

export interface ScrubberProgress {
  duration: number;
  progressSeconds: number;
  progressPercentage: number;
  liveProgressPercentage?: number;
  startTime?: Date;
  endTime?: Date;
}

export const getProgressPercentage = (position: number, duration: number) => {
  if (!duration) return 0;

  return +((100 * position) / duration).toFixed(2);
};

export const getPosition = (value: number, duration: number) => {
  return Math.round((value * duration) / 100);
};

export const getProgressPercentageStartOver = (progressSeconds: number, duration: number) => {
  return Math.min(getProgressPercentage(Math.max(progressSeconds, 0), duration), 100);
};

export const getProgressSeconds = ({ range, position }: SeekableRange) => {
  return position - range.start;
};

const MAX_VALID_POSITION_SECONDS = 24 * 60 * 60;

// Before a startover stream finishes rewinding the payload still describes the live
// stream, which reports an epoch-based position (seconds since January 1, 1970). Those updates must be ignored.
export const hasProgramRelativePosition = (seekableRange?: SeekableRange) => {
  return !!seekableRange && seekableRange.position < MAX_VALID_POSITION_SECONDS;
};

export const getOffsetInSeconds = (value: number, duration: number, start: number) => {
  return start + Math.floor((value * duration) / 100);
};

export const getLiveProgressPercentage = (durationSeconds: number, seekableEnd: number) => {
  return (seekableEnd / durationSeconds) * 100;
};

const formatSeconds = (value: number) => {
  const seconds = value % 60;

  const totalMinutes = value === seconds ? 0 : (value - seconds) / 60;

  const minutes = totalMinutes % 60;

  const hours = totalMinutes === minutes ? 0 : (totalMinutes - minutes) / 60;

  return { hours, minutes, seconds };
};

export const formatPlaytime = (value: number, max?: number): string => {
  const clampedValue = max !== undefined ? Math.min(value, max) : value;
  const { hours, minutes, seconds } = formatSeconds(Math.round(clampedValue));

  const hour = hours > 0 ? `${hours < 10 ? `0${hours}:` : `${hours}:`}` : '';
  const minute = minutes > 0 ? `${minutes < 10 ? `0${minutes}:` : `${minutes}:`}` : '00:';
  const second = seconds > 0 ? `${seconds < 10 ? `0${seconds}` : seconds}` : '00';

  return `${hour}${minute}${second}`;
};

interface Playtime {
  progress: string;
  remaining: string;
}

const getPlaytime = (progressSeconds: number, duration: number): Playtime => {
  const roundedPosition = Math.round(progressSeconds);

  return {
    progress: formatPlaytime(roundedPosition),
    remaining: formatPlaytime(Math.max(duration - roundedPosition, 0))
  };
};

const getTimeDisplayValue = (
  isStreamtypeLive: boolean,
  isPlacedLeft: boolean,
  playtime: Playtime,
  starttime?: Date,
  endtime?: Date
) => {
  if (isStreamtypeLive) {
    return formatDate(starttime ?? endtime ?? new Date(), 'hh:mm');
  } else {
    if (isPlacedLeft) {
      return playtime.progress;
    } else {
      return playtime.remaining;
    }
  }
};

export { getPlaytime, getTimeDisplayValue };
export type { Playtime };
